import { BoldText, DemiLightText, MediumText, RegularText } from '@/lib/Text';
import { Col, Row } from '@/lib/utils';
import React, { useState } from 'react';
import { StageReviewInfo } from '@/features/centerReview';
import SvgIcon from '@/components/SvgIcon';
import { colors } from '@/lib/colors';
import Dropdown from '@/components/DropDown/DropDown';
import { RatingStars } from '@/components/RatingStars';
import { CodeValueType } from './ModalAllReview';

type Props = {
    badgeNumber: number;
    label: string;
    stageData: StageReviewInfo;
    selectedQuestionCode: CodeValueType;
    onSelectQuestion: (question: CodeValueType) => void;
};

function ModalAllReviewHeader({ badgeNumber, label, stageData, selectedQuestionCode, onSelectQuestion }: Props) {
    // stageQuestionInfos로 드롭다운 옵션 구성
    const dropdownOptions = stageData.stageQuestionRankings.map((question) => ({
        code: question.stageQuestionCode,
        value: question.stageQuestionSubject,
    }));

    const handleSelectOption = (item: { code: string; value: string }) => {
        onSelectQuestion(item);
    };

    const isIncrease = stageData.stageAverageRatingChange >= 0;
    const changeValue = Math.abs(stageData.stageAverageRatingChange).toFixed(1);
    const positiveRate = stageData.stagePositiveAnswerSummary.stageAnswerRate || 0;
    const negativeRate = stageData.stageNegativeAnswerSummary.stageAnswerRate || 0;

    // 진행바 비율 정규화 (합이 100%를 초과하면 비율에 맞게 조정)
    const totalRate = positiveRate + negativeRate;
    const normalizedPositiveRate = totalRate > 100 ? (positiveRate / totalRate) * 100 : positiveRate;
    const normalizedNegativeRate = totalRate > 100 ? (negativeRate / totalRate) * 100 : negativeRate;

    return (
        <Col className="mb-[32px]">
            {/* 첫 번째 줄: 배지 + 회차명 + 별점 + 참여자 */}
            <Row className="items-center mb-[23px]">
                <div className="mr-[12px]">
                    <SvgIcon
                        name={`SvgReviewNum${badgeNumber}` as 'SvgReviewNum1' | 'SvgReviewNum2' | 'SvgReviewNum3'}
                        size={27}
                        fill={colors.Text_1}
                    />
                </div>
                <RegularText fontSize={20} lineHeight={27} color={'#191F28'} className="mr-[9px]">
                    {label}
                </RegularText>
                <Row className="items-center gap-[7px] mr-[14px]">
                    <SvgIcon name="SvgStar" fill="#3D73DD" size={15} />
                    <RegularText fontSize={20} lineHeight={27} color="#191F28">
                        {stageData.stageAverageRating.toFixed(1)}
                    </RegularText>
                </Row>
                <Row className="items-center gap-[8px]">
                    <SvgIcon name="SvgCheck" fill={colors.trans} size={12} stroke={colors.Text_1} strokeWidth={1.75} />
                    <RegularText fontSize={16} lineHeight={21} color={colors.Text_1} letterSpacing={-0.4}>
                        {/* 3자리 쉼표 */}
                        {stageData.stageReviewMemberCount.toLocaleString()}명 참여
                    </RegularText>
                </Row>
            </Row>

            {/* 두 번째 줄: 드롭다운 */}
            <div className="mb-[20px]">
                <Dropdown
                    options={dropdownOptions}
                    onSelect={handleSelectOption}
                    isAllOption={false}
                    bgColor={'#2D3132'}
                    borderRadius={50}
                    fontSize={18}
                    textColor="white"
                    arrowColor="white"
                    className="py-[12px]"
                    initialSelected={selectedQuestionCode.code}
                />
            </div>

            {/* 세 번째 줄: 점수와 통계 */}
            <Row className="items-start mb-[28px]">
                {/* 왼쪽: 별점 */}
                <div className="mr-[32px]">
                    <RatingStars rating={stageData.stageAverageRating} size={22} gap={0} showScore={true} fs={45} />
                </div>

                {/* 오른쪽: 1개월 전보다, 의견수, 진행바 */}
                <Col className="flex-1">
                    {/* 1개월 전보다 */}
                    <div className="flex items-center mb-[10px]">
                        <RegularText
                            fontSize={15}
                            lineHeight={21}
                            color={colors.Text_1}
                            className="mr-[7px]"
                            letterSpacing={-0.7}
                        >
                            1개월 전보다
                        </RegularText>
                        <SvgIcon
                            name={'SvgSortArrow'}
                            size={12}
                            fill={isIncrease ? colors.GREEN : colors.ERROR}
                            className={`mr-[3px] ${isIncrease ? '' : 'transform rotate-180'}`}
                        />
                        <RegularText fontSize={14} lineHeight={20} color={isIncrease ? colors.GREEN : colors.ERROR}>
                            {changeValue}
                        </RegularText>
                    </div>

                    {/* 의견수 */}
                    <div className="flex items-center mb-[12px]">
                        <SvgIcon
                            name="SvgLineHistory"
                            size={14}
                            fill={colors.trans}
                            stroke={colors.Text_1}
                            className="mr-[6px]"
                        />
                        <RegularText fontSize={15} lineHeight={21} color={colors.Text_1}>
                            {stageData.stageAnswerCount.toLocaleString()}회 의견
                        </RegularText>
                    </div>

                    {/* 진행바 */}
                    <div className="relative w-[133px] h-[3px] bg-[#F1F2F4] rounded-full overflow-hidden mb-[3px]">
                        <div
                            className="absolute left-0 top-0 h-full bg-[#59D696]"
                            style={{ width: `${Math.min(Math.max(normalizedPositiveRate, 0), 100)}%` }}
                        />
                        <div
                            className="absolute right-0 top-0 h-full bg-[#FF999B]"
                            style={{ width: `${Math.min(Math.max(normalizedNegativeRate, 0), 100)}%` }}
                        />
                    </div>

                    {/* 좋았어요/아쉬웠어요 비율 */}
                    <div className="flex w-[133px] items-center justify-between">
                        <div className="flex items-center gap-[6px]">
                            <RegularText fontSize={14} lineHeight={20} color={'#00A793'}>
                                {Math.round(positiveRate)}%
                            </RegularText>
                        </div>
                        <div className="flex items-center gap-[6px]">
                            <RegularText fontSize={14} lineHeight={20} color={'#FF553E'}>
                                {Math.round(negativeRate)}%
                            </RegularText>
                        </div>
                    </div>
                </Col>
            </Row>
            <Col className="flex w-full h-[1px] bg-[#E6E8EA]" />
        </Col>
    );
}

export default ModalAllReviewHeader;
